import { Button, Loader } from '@mantine/core';
import { sizes, variants } from '@uag/react-core';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { PlanToIfcJob } from 'app/modules/wallforming/components/plan-to-ifc/types/planToIfcTypes';

interface PlanToIfcJobStatusProps {
    job: PlanToIfcJob;
    isGenerating: boolean;
    isLiveConnected: boolean;
    elapsedSeconds: number;
    feedbackAgeSeconds: number;
    onCancel: () => void;
}

/** One status line, one timing line and the folded model reasoning of the running job. */
export const PlanToIfcJobStatus = ({
    job,
    isGenerating,
    isLiveConnected,
    elapsedSeconds,
    feedbackAgeSeconds,
    onCancel
}: PlanToIfcJobStatusProps) => {
    const { t } = useTranslation();
    const reasoningRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const reasoning = reasoningRef.current;
        if (reasoning) {
            reasoning.scrollTop = reasoning.scrollHeight;
        }
    }, [job.reasoning]);

    return (
        <div
            className="space-y-2 rounded-md border border-border-light p-3 text-xs"
            data-testid="plan-to-ifc-progress"
            role="status"
        >
            <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2 text-sm">
                    {isGenerating && <Loader size="xs" />}
                    <span>{job.progress}</span>
                </div>
                {isGenerating && (
                    <Button
                        disabled={job.status === 'cancelling'}
                        size={sizes.button.small}
                        variant={variants.button.secondary}
                        onClick={onCancel}
                    >
                        {t('cancel')}
                    </Button>
                )}
            </div>
            {isGenerating && (
                <div className="flex flex-wrap items-center gap-x-2 text-text-light">
                    <span>
                        {t('planToIfcElapsed', {
                            minutes: Math.floor(elapsedSeconds / 60),
                            seconds: elapsedSeconds % 60
                        })}
                    </span>
                    <span>·</span>
                    <span>{t('planToIfcFeedbackAge', { seconds: feedbackAgeSeconds })}</span>
                    <span>·</span>
                    <span
                        className="flex items-center gap-1"
                        data-testid="plan-to-ifc-live-status"
                        title={t('planToIfcBackgroundHint')}
                    >
                        <span
                            aria-hidden="true"
                            className="inline-block h-2 w-2 rounded-full"
                            style={{ background: isLiveConnected ? '#16a34a' : '#f59e0b' }}
                        />
                        {isLiveConnected ? t('planToIfcLiveConnected') : t('planToIfcLiveFallback')}
                    </span>
                </div>
            )}
            {job.reasoning && (
                <details open={isGenerating}>
                    <summary className="cursor-pointer font-medium text-text-light">{t('labelModelReasoning')}</summary>
                    <div
                        ref={reasoningRef}
                        className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap rounded-sm bg-background-gray-ultra-light p-2 text-text-light"
                        data-testid="plan-to-ifc-reasoning"
                    >
                        {job.reasoning}
                    </div>
                </details>
            )}
        </div>
    );
};
